import {Component, Inject, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AuditId, ProjectId, TestRunId} from "@webmate-web-core/shared/types/ids";
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {
    AppInstallationComponent,
    AppInstallationToolConfig
} from '@webmate-web-core/shared/components/device/appinstallation/app-installation.component';
import {
    DeployDeviceDialogComponent,
    DeployDeviceDialogToolConfig,
    DeployDeviceSelection
} from '@webmate-web-core/shared/tools/device-deployment/deploy-device-dialog.component';
import {createFinishedPackageInstallation, isPlatformCompatible} from '@webmate-web-core/shared/types/packages/package';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {DeployableService} from '@webmate-web-core/shared/services/device/deployable.service';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {catchError, map, mergeMap, shareReplay, switchMap, take} from 'rxjs/operators';
import {forkJoin, Observable, of} from 'rxjs';
import {Router} from '@angular/router';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import moment from 'moment';
import {
    defaultExpeditionComparisonConfigFromTest, expeditionComparisonParamsFromConfig,
    ExpeditionComparisonTestConfig, defaultSpecCheckConfigFromTest,
    specCheckParamsFromConfig,
    TestTemplate
} from '@webmate-web-core/shared/types/testmgmt';
import {
    CrossbrowserTestDialogComponent,
    CrossbrowserTestDialogToolConfig
} from '@webmate-web-core/shared/tools/cbt/crossbrowser-test-dialog.component';
import {
    RegressionTestDialogComponent,
    RegressionTestDialogToolConfig
} from '@webmate-web-core/shared/tools/regression-test/regression-test-dialog.component';
import {
    ManualWebsiteTestConfig,
    ManualWebsiteTestDialogComponent,
    ManualWebsiteTestDialogToolConfig
} from '@webmate-web-core/shared/tools/manual-website-test/manual-website-test-dialog.component';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {
    SpecCheckTestDialogComponent,
    SpecCheckTestDialogToolConfig
} from '@webmate-web-core/shared/tools/speccheck/spec-check-test-dialog.component';
import {WorkbenchOpenService} from '@webmate-web-core/shared/workbench-ipc';
import {AuditSearchRecord, AuditService} from '@webmate-web-core/shared/services/audit.service';
import {UserCustomizationService} from '@webmate-web-core/shared/services/user-customization.service';
import type {TestRunReportData} from '@webmate-web-core/shared/services/console-test-run.service';

type SelectTestCard = {
    id: string;
    title: string;
    description?: string;
    badgeLabel?: string;
    detailsTemplate?: TemplateRef<unknown>;
    detailsContext?: unknown;
    onClick: () => void;
};

type SelectTestCardGroup = {
    id: string;
    title: string;
    cards: SelectTestCard[];
};

export interface SelectTestInPlaceHandler {
    /**
     * Returns true if the workbench has applied the request locally and the dialog should not call
     * `WorkbenchOpenService.openWorkbench`. The handler is consulted only for `request.projectId`
     * matching the workbench's current project.
     */
    open(request: {
        projectId: ProjectId;
        auditId?: AuditId;
        deviceId?: string;
        activeTestRun?: TestRunId;
        autoStartManualTest?: boolean;
        suppressAutoResumeTestRun?: boolean;
        browserExpeditionStartConfig?: unknown;
    }): boolean;
}

export class SelectTestToolConfig implements WebmateToolConfig {
    toolName = "Select Test";
    scrollable = true;

    constructor(public projectId: ProjectId,
                public automatedUseCases: 'enabled' | 'disabled' | {allowedTemplateIds: string[]} = 'enabled',
                public inPlaceHandler?: SelectTestInPlaceHandler,
                public launchContext: 'webmate' | 'workbench' = 'webmate') {
    }
}

@Component({
    selector: 'wm-select-test',
    templateUrl: './select-test.component.html',
    styleUrls: ['./select-test.component.less']
})
export class SelectTestComponent implements OnInit {
    @ViewChild('auditCardDetails', {static: true}) auditCardDetailsTemplate!: TemplateRef<unknown>;

    cardGroups: SelectTestCardGroup[] = [];
    loading = true;
    private activeManualTestRuns$?: Observable<TestRunReportData[]>;

    constructor(@Inject(TOOL_CONTAINER_DATA) public data: SelectTestToolConfig, private toolService: ModalToolContainerService,
                private deviceService: DeviceService, private deployableService: DeployableService, private router: Router, private testService: TestService,
                private userService: UserService, private notificationService: WmNotificationService, private readonly featureService: FeatureService,
                private readonly workbenchOpenService: WorkbenchOpenService, private readonly auditService: AuditService,
                private readonly userCustomizationService: UserCustomizationService) {}

    ngOnInit() {
        this.deployableService.getDeployablesForProject$(this.data.projectId, undefined, true).pipe(take(1)).subscribe();
        forkJoin({
            templates: this.testService.getTestTemplatesInProject(this.data.projectId).pipe(take(1)),
            feature: this.featureService.getFeature("webmate.automated-spec-check").pipe(take(1)),
            recentAudits: this.featureService.getFeature("webmate.auditlab").pipe(
                take(1),
                switchMap(auditLabFeature => auditLabFeature?.isActive ? this.loadRecentAudits$() : of(undefined)),
                take(1)
            )
        }).subscribe(({templates, feature, recentAudits}) => {
            const baseTemplates = templates.filter(t => t.info.isBaseTemplate);
            const customTemplates = templates.filter(t => !t.info.isBaseTemplate);
            const specCheckAvailable = feature?.isActive ?? false;
            const automationTemplates = [...baseTemplates, ...customTemplates].filter(t => specCheckAvailable || t.testType !== "spec_check");
            const automatedCards = this.filterAutomatedTemplates(automationTemplates).map(t => this.createTemplateCard(t));
            this.cardGroups = [
                {id: 'device-tests', title: 'Manual Tests', cards: this.createDeviceCards()},
                {id: 'audit-lab', title: 'Audits', cards: recentAudits ? this.createAuditCards(recentAudits) : []},
                {id: 'automated-tests', title: 'Automated Tests', cards: automatedCards}
            ].filter(group => group.cards.length > 0);
            this.loading = false;
            this.prefetchActiveManualTestRuns();
        });
    }

    private createDeviceCards(): SelectTestCard[] {
        return [
            {
                id: 'start-manual-test',
                title: 'Start Manual Test',
                description: 'Start a manual test in the workbench.',
                onClick: () => this.startManualTest()
            },
            {
                id: 'app-test',
                title: 'App Test',
                description: 'Install and test a mobile app on a device of your choice.',
                onClick: () => this.openAppTest()
            },
            {
                id: 'manual-test',
                title: 'Manual Device Test',
                description: 'Interact with a desktop or mobile device in the workbench.',
                onClick: () => this.openManualTest()
            },
            {
                id: 'manual-website-test',
                title: 'Manual Website Test',
                description: 'Open a URL in a browser and test your website interactively.',
                onClick: () => this.openManualWebsiteTest()
            }
        ];
    }

    private loadRecentAudits$(): Observable<AuditSearchRecord[]> {
        const recentAuditIds = this.userCustomizationService.getMostRecentAudits(this.data.projectId);
        if (recentAuditIds.length === 0) {
            return of([]);
        }
        return this.auditService.queryAdhocAuditReports$(this.data.projectId, {
            filterSpec: {},
            groupingSpec: [],
            aggregationSpec: [],
            from: 0,
            size: 1000
        }).pipe(
            take(1),
            map(result => {
                const recordsById = new Map((result.items || []).map(record => [record.id, record]));
                const recentRecords = recentAuditIds
                    .map(auditId => recordsById.get(auditId))
                    .filter((audit): audit is AuditSearchRecord => !!audit && audit.projectId === this.data.projectId);
                this.userCustomizationService.setMostRecentAudits(this.data.projectId, recentRecords.map(audit => audit.id));
                return recentRecords;
            }),
            catchError(() => of([]))
        );
    }

    private createAuditCards(recentAudits: AuditSearchRecord[]): SelectTestCard[] {
        return [
            ...recentAudits.map(audit => ({
                id: `audit-${audit.id}`,
                title: audit.title,
                detailsTemplate: this.auditCardDetailsTemplate,
                detailsContext: audit,
                onClick: () => this.openAuditInWorkbench(audit.id)
            })),
            {
                id: 'select-audit-from-audit-lab',
                title: 'Select from Audit Lab',
                description: 'Open the Audit Lab to select an audit.',
                onClick: () => this.openAuditLab()
            }
        ];
    }

    private filterAutomatedTemplates(templates: TestTemplate[]): TestTemplate[] {
        const policy = this.data.automatedUseCases;
        if (policy === 'enabled') {
            return templates;
        }
        if (policy === 'disabled') {
            return [];
        }
        return templates.filter(template => policy.allowedTemplateIds.includes(template.info.id));
    }

    startManualTest() {
        this.promptForExistingManualTest(this.data.projectId, {
            onContinue: activeTestRun => {
                this.openWorkbench(this.data.projectId, undefined, {activeTestRun, autoStartManualTest: false});
                this.toolService.disposeToolContainer();
            },
            onStartNew: () => {
                this.openWorkbench(this.data.projectId, undefined, {autoStartManualTest: true});
                this.toolService.disposeToolContainer();
            }
        });
    }

    openAppTest() {
        this.toolService.openTool(AppInstallationComponent, new AppInstallationToolConfig(undefined, this.data.projectId, (virtualPackage, instrumented) => {
            const onDeploy = (selection: DeployDeviceSelection) => {
                const openWorkbench = (deviceId: string, options?: { activeTestRun?: TestRunId; autoStartManualTest?: boolean; suppressAutoResumeTestRun?: boolean }) =>
                    this.openWorkbench(selection.projectId, deviceId, options);

                const resolveWorkbenchOpen = (deviceId: string, afterOpen: () => void) => {
                    if (!selection.startTestOnDeploy) {
                        openWorkbench(deviceId);
                        afterOpen();
                        return;
                    }
                    this.promptForExistingManualTest(selection.projectId, {
                        onContinue: activeTestRun => {
                            openWorkbench(deviceId, {activeTestRun, autoStartManualTest: false});
                            afterOpen();
                        },
                        onStartNew: () => {
                            openWorkbench(deviceId, {autoStartManualTest: true});
                            afterOpen();
                        }
                    });
                };

                if (selection.existingDeviceId) {
                    const deviceId = selection.existingDeviceId;
                    let openCompleted = false;
                    let installCompleted = false;
                    const maybeDispose = () => {
                        if (openCompleted && installCompleted) {
                            this.toolService.disposeToolContainer();
                        }
                    };
                    resolveWorkbenchOpen(deviceId, () => {
                        openCompleted = true;
                        maybeDispose();
                    });
                    this.deviceService.installAppOnDevice$(deviceId, virtualPackage, instrumented).subscribe({
                        next: result => {
                            installCompleted = true;
                            maybeDispose();

                            if (result.errorMessage) {
                                this.notificationService.toastErrorNotification(result.errorMessage);
                            } else {
                                this.notificationService.toastNotification('App installation successful.');
                            }
                        },
                        error: () => {
                            installCompleted = true;
                            maybeDispose();
                            this.notificationService.toastWarningNotification('Could not confirm the app installation. Please check the device.');
                        }
                    });
                } else {
                    const requirements = {
                        ...selection.requirements,
                        [DevicePropertyNames.InstalledPackages]: createFinishedPackageInstallation(virtualPackage, instrumented)
                    };

                    this.deviceService.requestDeployment$(selection.projectId, requirements, selection.name).pipe(take(1)).subscribe(device => {
                        resolveWorkbenchOpen(device.id, () => {
                            this.toolService.disposeToolContainer();
                        });
                    });
                }
            };
            this.toolService.openTool(DeployDeviceDialogComponent, new DeployDeviceDialogToolConfig({
                projectId: this.data.projectId,
                platformFamily: 'mobile',
                selectedGroupingVariant: 'os',
                browserSessionDefault: true,
                enableGroupingVariantSwitcher: false,
                preFilter: (d) => isPlatformCompatible(virtualPackage, d.platform),
                includeDeployedDevices: true,
                showStartTestOnDeploy: true,
                startTestOnDeployDefault: true,
                toolSubtitle: `Select device for ${virtualPackage.name}`,
                onDeploy
            }));
        }));
    }

    openManualTest() {
        const onDeploy = (selection: DeployDeviceSelection) => {
            const openWorkbench = (deviceId: string, options?: { activeTestRun?: TestRunId; autoStartManualTest?: boolean; suppressAutoResumeTestRun?: boolean }) =>
                this.openWorkbench(selection.projectId, deviceId, options);

            const resolveWorkbenchOpen = (deviceId: string, afterOpen: () => void) => {
                if (!selection.startTestOnDeploy) {
                    openWorkbench(deviceId);
                    afterOpen();
                    return;
                }
                this.promptForExistingManualTest(selection.projectId, {
                    onContinue: activeTestRun => {
                        openWorkbench(deviceId, {activeTestRun, autoStartManualTest: false});
                        afterOpen();
                    },
                    onStartNew: () => {
                        openWorkbench(deviceId, {autoStartManualTest: true});
                        afterOpen();
                    }
                });
            };

            if (selection.existingDeviceId) {
                resolveWorkbenchOpen(selection.existingDeviceId, () => {
                    this.toolService.disposeToolContainer();
                });
            } else {
                this.deviceService.requestDeployment$(selection.projectId, selection.requirements, selection.name)
                    .pipe(take(1))
                    .subscribe(device => {
                        resolveWorkbenchOpen(device.id, () => {
                            this.toolService.disposeToolContainer();
                        });
                    });
            }
        };

        this.toolService.openTool(DeployDeviceDialogComponent, new DeployDeviceDialogToolConfig({
            projectId: this.data.projectId,
            platformFamily: 'desktop',
            enablePlatformFamilySwitcher: true,
            selectedGroupingVariant: 'os',
            browserSessionDefault: false,
            includeDeployedDevices: true,
            showStartTestOnDeploy: true,
            startTestOnDeployDefault: true,
            onDeploy
        }));
    }

    openManualWebsiteTest() {
        this.toolService.openTool(ManualWebsiteTestDialogComponent, new ManualWebsiteTestDialogToolConfig(this.data.projectId, (config) => {
            this.openManualWebsiteTestDeviceSelection(config);
        }));
    }

    private openManualWebsiteTestDeviceSelection(websiteTestConfig: ManualWebsiteTestConfig) {
        const onDeploy = (selection: DeployDeviceSelection) => {
            const openWorkbench = (deviceId: string, options?: { activeTestRun?: TestRunId; autoStartManualTest?: boolean; suppressAutoResumeTestRun?: boolean }) => {
                const browserExpeditionStartConfig = {
                    startUrl: websiteTestConfig.url,
                    browserType: selection.selectedBrowser?.browserType,
                    browserVersion: selection.selectedBrowser?.version
                };
                this.openWorkbench(selection.projectId, deviceId, options, browserExpeditionStartConfig);
            };

            const resolveWorkbenchOpen = (deviceId: string, afterOpen: () => void) => {
                if (!selection.startTestOnDeploy) {
                    openWorkbench(deviceId);
                    afterOpen();
                    return;
                }
                this.promptForExistingManualTest(selection.projectId, {
                    onContinue: activeTestRun => {
                        openWorkbench(deviceId, {activeTestRun, autoStartManualTest: false});
                        afterOpen();
                    },
                    onStartNew: () => {
                        openWorkbench(deviceId, {autoStartManualTest: true});
                        afterOpen();
                    }
                });
            };

            if (selection.existingDeviceId) {
                resolveWorkbenchOpen(selection.existingDeviceId, () => {
                    this.toolService.disposeToolContainer();
                });
            } else {
                this.deviceService.requestDeployment$(selection.projectId, selection.requirements, selection.name)
                    .pipe(take(1))
                    .subscribe(device => {
                        resolveWorkbenchOpen(device.id, () => {
                            this.toolService.disposeToolContainer();
                        });
                    });
            }
        };

        this.toolService.openTool(DeployDeviceDialogComponent, new DeployDeviceDialogToolConfig({
            projectId: this.data.projectId,
            platformFamily: 'desktop',
            enablePlatformFamilySwitcher: false,
            selectedGroupingVariant: 'browser',
            browserSessionDefault: false,
            preFilter: (d) => d.browser.some(b => b.browserType.toLowerCase() !== 'appium'),
            browserFilter: (b) => b.browserType.toLowerCase() !== 'appium' && (b.capabilities?.['selenium.webdriverSupport'] ?? false),
            enableGroupingVariantSwitcher: false,
            includeDeployedDevices: true,
            showAllowAutomation: false,
            allowAutomationDefault: true,
            showStartTestOnDeploy: true,
            startTestOnDeployDefault: true,
            toolSubtitle: `Select browser for ${websiteTestConfig.url}`,
            onDeploy
        }));
    }

    private createTemplateCard(template: TestTemplate): SelectTestCard {
        return {
            id: `template-${template.info.id}`,
            title: template.info.name,
            description: template.info.description,
            badgeLabel: template.info.isBaseTemplate ? 'Base' : 'Custom',
            onClick: () => this.createTest(template)
        };
    }

    private getVisualizationType(testTemplate: TestTemplate) {
        return testTemplate.params.find(p => p.name === 'testVisualisationType')?.default?.data;
    }

    private createTest(testTemplate: TestTemplate) {
        if (testTemplate.testType === 'expedition_comparison') {
            if (this.getVisualizationType(testTemplate) === 'crossbrowser_test') {
                this.executeCrossbrowserTest(testTemplate);
            } else if (this.getVisualizationType(testTemplate) === 'url_based_regression_test') {
                this.executeRegressionTest(testTemplate);
            } else {
                console.error('No execution tool for expedition comparison test with visualization type', this.getVisualizationType(testTemplate), 'found');
            }
        } else if (testTemplate.testType === 'spec_check') {
            this.executeSpecCheck(testTemplate);
        } else {
            console.error('No execution tool for type', testTemplate.testType, 'found');
        }
    }

    private createExpeditionComparisonCreationCallback(testTemplate: TestTemplate, displayType: string): (conf: ExpeditionComparisonTestConfig) => void {
        return (c: ExpeditionComparisonTestConfig) => {
            this.testService.createAndStartTestExecution(this.data.projectId, testTemplate.info.id, testTemplate.info.name, {
                type: "SingleTestRunCreationSpec",
                assignmentSpec: expeditionComparisonParamsFromConfig(c)
            }, displayType).pipe(take(1)).subscribe((createResult) => {
                 this.router.navigate(['projects', this.data.projectId, 'testlab', 'testruns', createResult.testRunId]);
                 this.toolService.disposeToolContainer();
            });
        };
    }

    private executeCrossbrowserTest(testTemplate: TestTemplate) {
        const executeCallback = this.createExpeditionComparisonCreationCallback(testTemplate, 'crossbrowsertest');
        this.toolService.openTool(CrossbrowserTestDialogComponent, new CrossbrowserTestDialogToolConfig(this.data.projectId, defaultExpeditionComparisonConfigFromTest(testTemplate), executeCallback, false));
    }

    private executeRegressionTest(testTemplate: TestTemplate) {
        const executeCallback = this.createExpeditionComparisonCreationCallback(testTemplate, 'regressiontest');
        this.toolService.openTool(RegressionTestDialogComponent, new RegressionTestDialogToolConfig(this.data.projectId, defaultExpeditionComparisonConfigFromTest(testTemplate), executeCallback, false));
    }

    private executeSpecCheck(testTemplate: TestTemplate) {
        const executeCallback = (config: any) => {
            this.testService.createAndStartTestExecution(this.data.projectId, testTemplate.info.id, config.name || testTemplate.info.name, {
                type: 'SingleTestRunCreationSpec',
                assignmentSpec: specCheckParamsFromConfig(config)
            }, undefined, config.automationPolicy).pipe(take(1)).subscribe((createResult) => {
                this.router.navigate(['projects', this.data.projectId, 'testlab', 'testruns', createResult.testRunId]);
                this.toolService.disposeToolContainer();
            });
        };

        this.toolService.openTool(
            SpecCheckTestDialogComponent,
            new SpecCheckTestDialogToolConfig(this.data.projectId, defaultSpecCheckConfigFromTest(testTemplate), executeCallback, true)
        );
    }


    private promptForExistingManualTest(
        projectId: ProjectId,
        handlers: { onContinue: (activeTestRun: TestRunId) => void; onStartNew: () => void }
    ) {
        this.getActiveManualTestRuns$(projectId).pipe(take(1)).subscribe({
            next: runs => {
                if (runs.length === 0) {
                    handlers.onStartNew();
                    return;
                }
                const sorted = [...runs].sort((a, b) => (b.creationTime || '').localeCompare(a.creationTime || ''));
                const testRun = sorted[0];
                const timestamp = testRun.creationTime ? moment(testRun.creationTime).fromNow() : 'recently';
                this.notificationService.openHtmlMessage(
                    'You already have a running manual test' +
                    ((testRun.name === 'Unnamed Test') ? '' : ' named <span class="dialog-highlight">\'' + testRun.name + '\'</span>') +
                    ' started <span class="dialog-highlight">' + timestamp + '</span>.' +
                    '<br>Do you want to continue it or start a new manual test for this workbench?',
                    'Continue Test',
                    () => handlers.onContinue(testRun.testRunId),
                    'Start New Test',
                    () => {
                        this.testService.finishTestRun(testRun.testRunId).pipe(take(1)).subscribe({
                            next: () => {
                                this.clearActiveManualTestRunsPrefetch();
                                handlers.onStartNew();
                            },
                            error: err => {
                                console.warn('Failed to finish existing test run before starting a new one.', err);
                                this.clearActiveManualTestRunsPrefetch();
                                handlers.onStartNew();
                            }
                        });
                    }
                );
            },
            error: err => {
                console.warn('Failed to look up active manual test runs.', err);
                handlers.onStartNew();
            }
        });
    }

    private prefetchActiveManualTestRuns(): void {
        this.getActiveManualTestRuns$(this.data.projectId).pipe(take(1)).subscribe({
            error: err => console.warn('Failed to prefetch active manual test runs.', err)
        });
    }

    private getActiveManualTestRuns$(projectId: ProjectId): Observable<TestRunReportData[]> {
        if (!this.activeManualTestRuns$) {
            this.activeManualTestRuns$ = this.userService.getCurrentUserData().pipe(
                take(1),
                mergeMap(user => this.testService.getActiveAdhocTestRunsForUser$(projectId, user.id)),
                shareReplay({bufferSize: 1, refCount: false})
            );
        }
        return this.activeManualTestRuns$;
    }

    private clearActiveManualTestRunsPrefetch(): void {
        this.activeManualTestRuns$ = undefined;
    }

    private openWorkbench(projectId: ProjectId, deviceId?: string,
                          options?: { activeTestRun?: TestRunId; autoStartManualTest?: boolean; suppressAutoResumeTestRun?: boolean },
                          browserExpeditionStartConfig?: unknown): void {
        const request = {
            projectId,
            deviceId,
            activeTestRun: options?.activeTestRun,
            autoStartManualTest: options?.autoStartManualTest,
            suppressAutoResumeTestRun: options?.suppressAutoResumeTestRun,
            browserExpeditionStartConfig
        };
        if (this.data.inPlaceHandler && projectId === this.data.projectId) {
            const handled = this.data.inPlaceHandler.open(request);
            if (handled) {
                return;
            }
        }
        this.workbenchOpenService.openWorkbench(request, {
            blockedPopupMessage: 'Your browser has blocked this console, please allow pop-ups / new tabs for this webpage!'
        }).pipe(take(1)).subscribe();
    }

    private openAuditInWorkbench(auditId: AuditId): void {
        const request = {
            projectId: this.data.projectId,
            auditId
        };
        if (this.data.inPlaceHandler?.open(request)) {
            this.toolService.disposeToolContainer();
            return;
        }
        this.workbenchOpenService.openWorkbench(request, {
            blockedPopupMessage: 'Your browser has blocked this console, please allow pop-ups / new tabs for this webpage!'
        }).pipe(take(1)).subscribe(() => this.toolService.disposeToolContainer());
    }

    private openAuditLab(): void {
        if (this.data.launchContext === 'webmate') {
            this.router.navigate(['projects', this.data.projectId, 'auditlab', 'audits']);
            this.toolService.disposeToolContainer();
            return;
        }
        const url = this.router.serializeUrl(this.router.createUrlTree(['projects', this.data.projectId, 'auditlab', 'audits']));
        window.open(url, '_blank');
        this.toolService.disposeToolContainer();
    }
}
